import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import { useToastContext } from '../App';
import { useWebSocket } from '../hooks/useWebSocket';

function Players() {
  const [servers, setServers] = useState([]);
  const [selectedServer, setSelectedServer] = useState('');
  const [activeTab, setActiveTab] = useState('online');
  const [onlinePlayers, setOnlinePlayers] = useState([]);
  const [whitelist, setWhitelist] = useState([]);
  const [banned, setBanned] = useState([]);
  const [newPlayer, setNewPlayer] = useState('');
  const addToast = useToastContext();

  // Whitelist toggle state
  const [whitelistEnabled, setWhitelistEnabled] = useState(false);
  const [enforceWhitelist, setEnforceWhitelist] = useState(false);
  const [togglingWl, setTogglingWl] = useState(false);

  // Whitelist requests
  const [requests, setRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);

  // OPs
  const [ops, setOps] = useState([]);
  const [opPlayerName, setOpPlayerName] = useState('');
  const [opDropdownValue, setOpDropdownValue] = useState('');

  // Action History
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyFilter, setHistoryFilter] = useState({ action: '', targetPlayer: '' });

  // WebSocket for whitelist notifications
  const { whitelistNotifications, clearWhitelistNotifications } = useWebSocket(selectedServer);

  useEffect(() => {
    loadServers();
  }, []);

  useEffect(() => {
    if (selectedServer) {
      loadPlayers();
      loadWhitelistStatus();
      const interval = setInterval(loadPlayers, 5000);
      return () => clearInterval(interval);
    }
  }, [selectedServer]);

  // Load tab-specific data when switching tabs
  useEffect(() => {
    if (!selectedServer) return;
    if (activeTab === 'requests') {
      loadRequests();
    } else if (activeTab === 'ops') {
      loadOps();
      loadWhitelistStatus();
    } else if (activeTab === 'history') {
      loadHistory();
    }
  }, [activeTab, selectedServer]);

  // Reload requests when we get a whitelist notification
  useEffect(() => {
    if (whitelistNotifications.length > 0 && activeTab === 'requests') {
      loadRequests();
    }
  }, [whitelistNotifications]);

  async function loadServers() {
    try {
      const data = await api.getServers();
      setServers(data);
      if (data.length > 0 && !selectedServer) {
        setSelectedServer(data[0].id);
      }
    } catch (e) {
      console.error(e);
    }
  }

  async function loadPlayers() {
    if (!selectedServer) return;
    try {
      const [online, wl, ban] = await Promise.all([
        api.getOnlinePlayers(selectedServer),
        api.getWhitelist(selectedServer),
        api.getBanned(selectedServer),
      ]);
      setOnlinePlayers(online.players || []);
      setWhitelist(wl || []);
      setBanned(ban || []);
    } catch (e) {
      console.error(e);
    }
  }

  async function loadWhitelistStatus() {
    if (!selectedServer) return;
    try {
      const status = await api.getWhitelistStatus(selectedServer);
      setWhitelistEnabled(status.whitelistEnabled || false);
      setEnforceWhitelist(status.enforceWhitelist || false);
    } catch (e) {
      console.error(e);
    }
  }

  async function loadRequests() {
    if (!selectedServer) return;
    setRequestsLoading(true);
    try {
      const data = await api.getWhitelistRequests(selectedServer);
      setRequests(data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setRequestsLoading(false);
    }
  }

  async function loadOps() {
    if (!selectedServer) return;
    try {
      const data = await api.getOps(selectedServer);
      setOps(data || []);
    } catch (e) {
      console.error(e);
    }
  }

  async function loadHistory() {
    if (!selectedServer) return;
    setHistoryLoading(true);
    try {
      const filters = {
        serverId: selectedServer,
        limit: 200,
      };
      if (historyFilter.action) filters.action = historyFilter.action;
      if (historyFilter.targetPlayer) filters.targetPlayer = historyFilter.targetPlayer;
      const data = await api.getActionHistory(filters);
      setHistory(data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setHistoryLoading(false);
    }
  }

  // Reload history when filters change
  const handleFilterChange = useCallback((key, value) => {
    setHistoryFilter(prev => {
      const next = { ...prev, [key]: value };
      return next;
    });
  }, []);

  useEffect(() => {
    if (activeTab === 'history' && selectedServer) {
      loadHistory();
    }
  }, [historyFilter]);

  // --- Actions ---

  async function handleAddWhitelist(e) {
    e.preventDefault();
    if (!newPlayer.trim()) return;
    try {
      await api.addToWhitelist(selectedServer, newPlayer.trim());
      addToast(`${newPlayer} zur Whitelist hinzugefuegt`, "success");
      setNewPlayer('');
      loadPlayers();
    } catch (e) {
      addToast(e.message, "error");
    }
  }

  async function handleRemoveWhitelist(name) {
    try {
      await api.removeFromWhitelist(selectedServer, name);
      addToast(`${name} von Whitelist entfernt`, "success");
      loadPlayers();
    } catch (e) {
      addToast(e.message, "error");
    }
  }

  async function handleBan(name) {
    const reason = window.prompt(`Grund fuer Ban von ${name}:`);
    if (reason === null) return;
    try {
      await api.banPlayer(selectedServer, name || newPlayer, reason);
      addToast("Spieler gebannt", "success");
      setNewPlayer('');
      loadPlayers();
    } catch (e) {
      addToast(e.message, "error");
    }
  }

  async function handleUnban(name) {
    try {
      await api.unbanPlayer(selectedServer, name);
      addToast(`${name} entbannt`, "success");
      loadPlayers();
    } catch (e) {
      addToast(e.message, "error");
    }
  }

  async function handleKick(name) {
    try {
      await api.kickPlayer(selectedServer, name);
      addToast(`${name} gekickt`, "success");
    } catch (e) {
      addToast(e.message, "error");
    }
  }

  async function handleOp(name) {
    try {
      await api.opPlayer(selectedServer, name);
      addToast(`${name} ist jetzt OP`, "success");
      // Minecraft schreibt ops.json asynchron — kurz warten dann neu laden
      setTimeout(loadOps, 2000);
    } catch (e) {
      addToast(e.message, "error");
    }
  }

  async function handleDeop(name) {
    try {
      await api.deopPlayer(selectedServer, name);
      addToast(`${name} ist kein OP mehr`, "success");
      setTimeout(loadOps, 1500);
    } catch (e) {
      addToast(e.message, "error");
    }
  }

  async function handleToggleWhitelist() {
    setTogglingWl(true);
    try {
      const newState = !whitelistEnabled;
      await api.toggleWhitelist(selectedServer, newState);
      setWhitelistEnabled(newState);
      if (newState) setEnforceWhitelist(true);
      addToast(`Whitelist ${newState ? "aktiviert" : "deaktiviert"}`, "success");
    } catch (e) {
      addToast(e.message, "error");
    } finally {
      setTogglingWl(false);
    }
  }

  async function handleApproveRequest(requestId) {
    try {
      await api.approveWhitelistRequest(selectedServer, requestId);
      addToast("Anfrage genehmigt — Spieler zur Whitelist hinzugefuegt", "success");
      loadRequests();
      loadPlayers();
    } catch (e) {
      addToast(e.message, "error");
    }
  }

  async function handleDenyRequest(requestId, playerName) {
    const notes = window.prompt(`Grund fuer Ablehnung von ${playerName} (optional):`);
    if (notes === null) return; // cancelled
    try {
      await api.denyWhitelistRequest(selectedServer, requestId, notes || '');
      addToast("Anfrage abgelehnt", "success");
      loadRequests();
    } catch (e) {
      addToast(e.message, "error");
    }
  }

  const currentServer = servers.find(s => s.id === selectedServer);
  const pendingRequests = requests.filter(r => r.status === 'pending');

  return (
    <div>
      <div className="page-header">
        <h2>Spieler-Verwaltung</h2>
      </div>

      <div className="form-group">
        <label>Server auswaehlen</label>
        <select
          className="form-select"
          value={selectedServer}
          onChange={e => {
            setSelectedServer(e.target.value);
            setActiveTab('online');
          }}
        >
          <option value="">-- Server waehlen --</option>
          {servers.map(s => (
            <option key={s.id} value={s.id}>
              {s.name} ({s.status})
            </option>
          ))}
        </select>
      </div>

      {selectedServer && (
        <>
          {/* Main tabs */}
          <div className="tabs">
            <button className={`tab ${activeTab === 'online' ? 'active' : ''}`} onClick={() => setActiveTab('online')}>
              Online ({onlinePlayers.length})
            </button>
            <button className={`tab ${activeTab === 'whitelist' ? 'active' : ''}`} onClick={() => setActiveTab('whitelist')}>
              Whitelist ({whitelist.length})
            </button>
            <button className={`tab ${activeTab === 'banned' ? 'active' : ''}`} onClick={() => setActiveTab('banned')}>
              Gebannt ({banned.length})
            </button>
            <button className={`tab ${activeTab === 'requests' ? 'active' : ''}`} onClick={() => setActiveTab('requests')}>
              Anfragen
              {pendingRequests.length > 0 && (
                <span className="wl-notification-badge">{pendingRequests.length}</span>
              )}
            </button>
            <button className={`tab ${activeTab === 'ops' ? 'active' : ''}`} onClick={() => setActiveTab('ops')}>
              OPs ({ops.length})
            </button>
            <button className={`tab ${activeTab === 'history' ? 'active' : ''}`} onClick={() => setActiveTab('history')}>
              Verlauf
            </button>
          </div>

          {/* ===== Online Players ===== */}
          {activeTab === 'online' && (
            <div className="card">
              <h3>Online Spieler</h3>
              {currentServer?.status !== 'running' ? (
                <div className="empty-state">
                  <p>Server ist nicht gestartet</p>
                </div>
              ) : onlinePlayers.length === 0 ? (
                <div className="empty-state">
                  <p>Keine Spieler online</p>
                </div>
              ) : (
                <div>
                  {onlinePlayers.map(name => (
                    <div key={name} className="player-item">
                      <div className="player-info">
                        <img
                          className="player-avatar"
                          src={`https://mc-heads.net/avatar/${name}/36`}
                          alt={name}
                          onError={e => { e.target.style.display = 'none'; }}
                        />
                        <span className="fw-bold">{name}</span>
                        <span className="tag enabled">Online</span>
                      </div>
                      <div className="player-actions">
                        <button className="btn btn-secondary btn-sm" onClick={() => handleOp(name)}>OP</button>
                        <button className="btn btn-warning btn-sm" onClick={() => handleKick(name)}>Kick</button>
                        <button className="btn btn-danger btn-sm" onClick={() => handleBan(name)}>Ban</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ===== Whitelist ===== */}
          {activeTab === 'whitelist' && (
            <div className="card">
              <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3>Whitelist</h3>
                {/* Whitelist toggle */}
                <div className="whitelist-toggle" onClick={!togglingWl ? handleToggleWhitelist : undefined}>
                  <div className={`whitelist-toggle-switch ${whitelistEnabled ? 'active' : ''}`}>
                    <div className="whitelist-toggle-knob" />
                  </div>
                  <span className="whitelist-toggle-label">
                    {whitelistEnabled ? "Whitelist aktiv" : "Whitelist inaktiv"}
                  </span>
                </div>
              </div>
              <form onSubmit={handleAddWhitelist} className="search-bar mb-4">
                <input
                  className="form-input"
                  value={newPlayer}
                  onChange={e => setNewPlayer(e.target.value)}
                  placeholder="Spielername eingeben..."
                />
                <button className="btn btn-primary" type="submit">Hinzufuegen</button>
              </form>

              {whitelist.length === 0 ? (
                <div className="empty-state">
                  <p>Whitelist ist leer</p>
                </div>
              ) : (
                <div>
                  {whitelist.map(player => (
                    <div key={player.name} className="player-item">
                      <div className="player-info">
                        <img
                          className="player-avatar"
                          src={`https://mc-heads.net/avatar/${player.name}/36`}
                          alt={player.name}
                          onError={e => { e.target.style.display = 'none'; }}
                        />
                        <span className="fw-bold">{player.name}</span>
                      </div>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleRemoveWhitelist(player.name)}
                      >
                        Entfernen
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ===== Banned ===== */}
          {activeTab === 'banned' && (
            <div className="card">
              <div className="card-header">
                <h3>Gebannte Spieler</h3>
              </div>
              <form onSubmit={(e) => { e.preventDefault(); handleBan(newPlayer); }} className="search-bar mb-4">
                <input
                  className="form-input"
                  value={newPlayer}
                  onChange={e => setNewPlayer(e.target.value)}
                  placeholder="Spielername zum Bannen..."
                />
                <button className="btn btn-danger" type="submit">Bannen</button>
              </form>

              {banned.length === 0 ? (
                <div className="empty-state">
                  <p>Keine gebannten Spieler</p>
                </div>
              ) : (
                <div>
                  {banned.map(player => (
                    <div key={player.name} className="player-item">
                      <div className="player-info">
                        <span className="fw-bold">{player.name}</span>
                        <span className="text-sm text-muted">
                          Grund: {player.reason || 'Kein Grund'}
                        </span>
                      </div>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => handleUnban(player.name)}
                      >
                        Entbannen
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ===== Anfragen (Whitelist Requests) ===== */}
          {activeTab === 'requests' && (
            <div className="card">
              <div className="card-header">
                <h3>Whitelist-Anfragen</h3>
              </div>

              {requestsLoading ? (
                <div className="empty-state"><div className="spinner" /></div>
              ) : requests.length === 0 ? (
                <div className="empty-state">
                  <p>Keine Whitelist-Anfragen</p>
                  <p className="text-sm text-muted">
                    Anfragen werden automatisch erstellt, wenn ein Spieler versucht einem Server mit aktiver Whitelist beizutreten.
                  </p>
                </div>
              ) : (
                <div>
                  {/* Pending first, then others */}
                  {requests
                    .sort((a, b) => {
                      if (a.status === 'pending' && b.status !== 'pending') return -1;
                      if (a.status !== 'pending' && b.status === 'pending') return 1;
                      return new Date(b.requestedAt) - new Date(a.requestedAt);
                    })
                    .map(req => (
                      <div key={req.id} className="request-card">
                        <div className="request-card-info">
                          <img
                            className="player-avatar"
                            src={`https://mc-heads.net/avatar/${req.playerName}/36`}
                            alt={req.playerName}
                            onError={e => { e.target.style.display = 'none'; }}
                          />
                          <div className="request-card-details">
                            <span className="request-card-name">{req.playerName}</span>
                            <span className="request-card-meta">
                              {new Date(req.requestedAt).toLocaleString('de-DE')}
                              {req.playerUuid && (
                                <span style={{ marginLeft: 8, fontSize: 13, color: '#555' }}>
                                  UUID: {req.playerUuid.substring(0, 8)}...
                                </span>
                              )}
                            </span>
                            {req.notes && (
                              <span className="request-card-meta" style={{ color: 'var(--mc-gold)' }}>
                                Notiz: {req.notes}
                              </span>
                            )}
                          </div>
                          <span className={`request-badge ${req.status}`}>
                            {req.status === 'pending' ? 'Ausstehend' : req.status === 'approved' ? 'Genehmigt' : 'Abgelehnt'}
                          </span>
                        </div>
                        {req.status === 'pending' && (
                          <div className="request-card-actions">
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() => handleApproveRequest(req.id)}
                            >
                              Annehmen
                            </button>
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() => handleDenyRequest(req.id, req.playerName)}
                            >
                              Ablehnen
                            </button>
                          </div>
                        )}
                        {req.status === 'approved' && req.resolvedBy && (
                          <div className="request-card-meta" style={{ flexShrink: 0 }}>
                            von {req.resolvedBy}
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              )}
            </div>
          )}

          {/* ===== OPs ===== */}
          {activeTab === 'ops' && (
            <div className="card">
              <div className="card-header">
                <h3>Operatoren (OPs)</h3>
              </div>

              {ops.length === 0 ? (
                <div className="empty-state">
                  <p>Keine OPs konfiguriert</p>
                </div>
              ) : (
                <div>
                  {ops.map(op => (
                    <div key={op.uuid || op.name} className="op-item">
                      <div className="op-info">
                        <img
                          className="player-avatar"
                          src={`https://mc-heads.net/avatar/${op.name}/36`}
                          alt={op.name}
                          onError={e => { e.target.style.display = 'none'; }}
                        />
                        <span className="fw-bold">{op.name}</span>
                        <span className="op-level">Level {op.level}</span>
                      </div>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDeop(op.name)}
                      >
                        OP entfernen
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div style={{ marginTop: 16, paddingTop: 16, borderTop: '2px solid rgba(255,255,255,0.05)' }}>
                <h4 style={{ marginBottom: 8 }}>Spieler zum OP machen</h4>
                {(() => {
                  const availableFromWl = whitelist.filter(
                    w => !ops.some(o => o.name.toLowerCase() === w.name.toLowerCase())
                  );

                  if (enforceWhitelist) {
                    // Whitelist enforced: only allow picking from whitelist
                    return availableFromWl.length === 0 ? (
                      <p className="text-muted">Alle Whitelist-Spieler sind bereits OP.</p>
                    ) : (
                      <form
                        onSubmit={(e) => {
                          e.preventDefault();
                          if (opDropdownValue) {
                            handleOp(opDropdownValue);
                            setOpDropdownValue('');
                          }
                        }}
                        className="search-bar"
                      >
                        <select
                          className="form-select"
                          value={opDropdownValue}
                          onChange={e => setOpDropdownValue(e.target.value)}
                        >
                          <option value="">-- Spieler waehlen --</option>
                          {availableFromWl.map(p => (
                            <option key={p.uuid || p.name} value={p.name}>{p.name}</option>
                          ))}
                        </select>
                        <button className="btn btn-primary" type="submit" disabled={!opDropdownValue}>
                          OP geben
                        </button>
                      </form>
                    );
                  } else {
                    // Whitelist not enforced: dropdown (if whitelist has entries) + free text
                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {availableFromWl.length > 0 && (
                          <form
                            onSubmit={(e) => {
                              e.preventDefault();
                              if (opDropdownValue) {
                                handleOp(opDropdownValue);
                                setOpDropdownValue('');
                              }
                            }}
                            className="search-bar"
                          >
                            <select
                              className="form-select"
                              value={opDropdownValue}
                              onChange={e => setOpDropdownValue(e.target.value)}
                            >
                              <option value="">-- Aus Whitelist waehlen --</option>
                              {availableFromWl.map(p => (
                                <option key={p.uuid || p.name} value={p.name}>{p.name}</option>
                              ))}
                            </select>
                            <button className="btn btn-primary" type="submit" disabled={!opDropdownValue}>
                              OP geben
                            </button>
                          </form>
                        )}
                        <form
                          onSubmit={(e) => {
                            e.preventDefault();
                            if (opPlayerName.trim()) {
                              handleOp(opPlayerName.trim());
                              setOpPlayerName('');
                            }
                          }}
                          className="search-bar"
                        >
                          <input
                            className="form-input"
                            value={opPlayerName}
                            onChange={e => setOpPlayerName(e.target.value)}
                            placeholder="Spielername manuell eingeben..."
                          />
                          <button className="btn btn-primary" type="submit" disabled={!opPlayerName.trim()}>
                            OP geben
                          </button>
                        </form>
                      </div>
                    );
                  }
                })()}
              </div>
            </div>
          )}

          {/* ===== Verlauf (Action History) ===== */}
          {activeTab === 'history' && (
            <div className="card">
              <div className="card-header">
                <h3>Aktions-Verlauf</h3>
              </div>

              <div className="history-filters">
                <select
                  value={historyFilter.action}
                  onChange={e => handleFilterChange('action', e.target.value)}
                >
                  <option value="">Alle Aktionen</option>
                  <option value="whitelist_add">Whitelist hinzufuegen</option>
                  <option value="whitelist_remove">Whitelist entfernen</option>
                  <option value="ban">Bannen</option>
                  <option value="unban">Entbannen</option>
                  <option value="kick">Kicken</option>
                  <option value="op">OP geben</option>
                  <option value="deop">OP entfernen</option>
                  <option value="whitelist_approve">Anfrage genehmigt</option>
                  <option value="whitelist_deny">Anfrage abgelehnt</option>
                  <option value="whitelist_toggle">Whitelist umgeschaltet</option>
                </select>
                <input
                  type="text"
                  value={historyFilter.targetPlayer}
                  onChange={e => handleFilterChange('targetPlayer', e.target.value)}
                  placeholder="Spieler filtern..."
                />
              </div>

              {historyLoading ? (
                <div className="empty-state"><div className="spinner" /></div>
              ) : history.length === 0 ? (
                <div className="empty-state">
                  <p>Keine Aktionen gefunden</p>
                </div>
              ) : (
                <div className="history-list">
                  {history.map((item, i) => (
                    <div key={item.id || i} className="history-item">
                      <span className="history-time">
                        {new Date(item.timestamp).toLocaleString('de-DE', {
                          day: '2-digit', month: '2-digit', year: '2-digit',
                          hour: '2-digit', minute: '2-digit'
                        })}
                      </span>
                      <span className={`history-action ${item.action}`}>
                        {formatAction(item.action)}
                      </span>
                      <span className="history-detail">
                        <strong>{item.targetPlayer}</strong>
                        {item.details?.reason && ` — ${item.details.reason}`}
                        {item.details?.notes && ` — ${item.details.notes}`}
                        <span className="text-muted" style={{ marginLeft: 8, fontSize: 14 }}>
                          von {item.performedBy}
                        </span>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function formatAction(action) {
  const map = {
    'whitelist_add': 'WL+',
    'whitelist_remove': 'WL-',
    'ban': 'BAN',
    'unban': 'UNBAN',
    'kick': 'KICK',
    'op': 'OP+',
    'deop': 'OP-',
    'approve_request': 'ANFRAGE OK',
    'deny_request': 'ANFRAGE X',
    'whitelist_toggle': 'WL TOGGLE',
  };
  return map[action] || action;
}

export default Players;
